// src/components/InboundTable.tsx
import React, { useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { useResizableColumns, ResCol } from '../hooks/useResizableColumns';
import { Table, ConfigProvider, Tag } from 'antd';
import ruRU from 'antd/es/locale/ru_RU';
import { formatDateTime } from '../utils/dates';
import { formatCurrency } from '../utils/numbers';

type InboundLine = {
  name?: string;
  sku?: string;
  qty?: number;
  unitCost?: number;
  totalCost?: number;
  quality?: 'Оригинал' | 'Копия' | 'original' | 'copy' | string; // НОВОЕ
};

type Inbound = {
  id?: string;
  createdAt: string;
  supplier?: string;
  lines?: InboundLine[];
  qty?: number;
  totalCost?: number;
  status?: string;
};

type Row = {
  key: string;
  inboundId: string;
  dateTs: number;
  supplier: string;
  status: string;
  name: string;
  quality: string;         // НОВОЕ
  sku: string;
  qty: number;
  unitCost: number | null;
  totalCost: number | null;
};

function statusLabel(raw?: string): string {
  const key = String(raw ?? '').trim().toLowerCase();
  if (key === 'ready') return 'Готово';
  if (key === 'processing') return 'В процессе';
  if (key === 'in_transit' || key === 'in-transit' || key === 'transit') return 'На пути';
  return '—';
}

const qualityLabel = (q: InboundLine['quality']): string => {
  if (q == null) return '—';
  const s = String(q).toLowerCase();
  if (s === 'original' || s === 'оригинал' || s === 'orig') return 'Оригинал';
  if (s === 'copy' || s === 'копия' || s === 'replica') return 'Копия';
  return String(q);
};

type Props = { inbounds: Inbound[]; maxRows?: number; };

const InboundTable: React.FC<Props> = ({ inbounds, maxRows = 500 }) => {
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];

    for (const ib of (inbounds ?? [])) {
      const seqNum = (ib as any).seqNum;
      const inboundId = seqNum
        ? `ПТП-${String(seqNum).padStart(4, '0')}`
        : (ib as any).id ?? '—';
      const dateTs = new Date((ib as any).createdAt).getTime();
      const supplier = (ib as any).supplier ?? '—';
      const stat = statusLabel((ib as any)?.status);
      const lines = Array.isArray((ib as any).lines) ? (ib as any).lines : [];

      if (lines.length) {
        lines.forEach((it: any, idx: number) => {
          const qty = Number(it.qty ?? it.quantity ?? 0);
          const unit = it.unitCost != null ? Number(it.unitCost) : null;
          const total = it.totalCost != null ? Number(it.totalCost)
                       : unit != null ? unit * qty : null;

          out.push({
            key: `${inboundId}-${idx}`,
            inboundId, dateTs, supplier, status: stat,
            name: it.name || '—',
            quality: qualityLabel(it.quality),
            sku: it.sku || '—',
            qty, unitCost: unit, totalCost: total,
          });
        });
      } else {
        out.push({
          key: `${inboundId}-0`,
          inboundId, dateTs, supplier, status: stat,
          name: '(без позиций)', quality: '—', sku: '—',
          qty: Number((ib as any).qty ?? 0),
          unitCost: null, totalCost: (ib as any).totalCost ?? null,
        });
      }
    }

    out.sort((a, b) => b.dateTs - a.dateTs);
    return out.slice(0, maxRows);
  }, [inbounds, maxRows]);

  const baseColumns: ResCol<Row>[] = [
    { key: 'inboundId', title: 'Поступление', dataIndex: 'inboundId',
      width: 180, minWidth: 130, align: 'left', ellipsis: true,
      sorter: (a,b) => a.inboundId.localeCompare(b.inboundId, 'ru') },
    { key: 'supplier', title: 'Поставщик', dataIndex: 'supplier',
      width: 160, minWidth: 150, align: 'left', ellipsis: true,
      sorter: (a,b) => a.supplier.localeCompare(b.supplier, 'ru') },
      { key: 'dateTs', title: 'Дата', dataIndex: 'dateTs',
      width: 170, minWidth: 150, align: 'left',
      render: (ts: number) => formatDateTime(ts),
      sorter: (a,b) => a.dateTs - b.dateTs, defaultSortOrder: 'descend' as const },
    { key: 'sku', title: 'SKU', dataIndex: 'sku',
      width: 130, minWidth: 110, align: 'left', ellipsis: true,
      sorter: (a,b) => a.sku.localeCompare(b.sku, 'ru') },
    { key: 'status', title: 'Статус', dataIndex: 'status',
      width: 150, minWidth: 130, align: 'left',
      render: (v: string) => {
        const color = v === 'Готово' ? 'blue' : v === 'В процессе' ? 'orange' : v === 'На пути' ? 'cyan' : 'default';
        return <Tag color={color}>{v}</Tag>;
      },
      sorter: (a,b) => a.status.localeCompare(b.status, 'ru') },
    { key: 'name', title: 'Товар', dataIndex: 'name',
      width: 160, minWidth: 140, align: 'left', ellipsis: true,
      sorter: (a,b) => a.name.localeCompare(b.name, 'ru') },
    { key: 'quality', title: 'Тип товара', dataIndex: 'quality',
      width: 140, minWidth: 120, align: 'left', ellipsis: true,
      sorter: (a,b) => a.quality.localeCompare(b.quality, 'ru') },
    { key: 'qty', title: 'Кол-во', dataIndex: 'qty',
      width: 100, minWidth: 90, align: 'left',
      sorter: (a,b) => a.qty - b.qty },
    { key: 'unitCost', title: 'Цена закупки', dataIndex: 'unitCost',
      width: 140, minWidth: 110, align: 'left',
      render: (v) => (v == null ? '—' : formatCurrency(v, 'AMD')),
      sorter: (a,b) => (a.unitCost ?? -Infinity) - (b.unitCost ?? -Infinity) },
    { key: 'totalCost', title: 'Сумма', dataIndex: 'totalCost',
      width: 130, minWidth: 110, align: 'left',
      render: (v) => (v == null ? '—' : <b>{formatCurrency(v, 'AMD')}</b>),
      sorter: (a,b) => (a.totalCost ?? -Infinity) - (b.totalCost ?? -Infinity) },
  ];

  const { columns, components } = useResizableColumns<Row>(baseColumns, {
    storageKey: 'table.inbounds.cols.v4',
    enableVisibility: true,
    enableReorder: true,
  });

  const locale = { ...ruRU, Pagination: { ...(ruRU as any).Pagination, items_per_page: '' } } as any;

  return (
    <ConfigProvider locale={locale}>
      <Table<Row>
        size="small"
        rowKey="key"
        dataSource={rows}
        columns={columns as ColumnsType<Row>}
        components={components}
        scroll={{ x: 'max-content' }}
        pagination={{
          current, pageSize, position: ['bottomRight'], showSizeChanger: true,
          pageSizeOptions: ['20','50','100'],
          onChange: (p, s) => { setCurrent(p); setPageSize(s); },
          showLessItems: true,
        }}
      />
    </ConfigProvider>
  );
};

export default InboundTable;
